import React, { useState } from 'react'; 
import UnifiedGallery from '../components/UnifiedGallery';
import useStandardGallery from '../hooks/useStandardGallery';

/**
 * RawrockPage - COMPREHENSIVE GALLERY CONFIGURATION EXAMPLE
 * 
 * This page demonstrates ALL possible parameters for the useStandardGallery hook.
 * Use this as a reference when configuring other gallery pages.
 * 
 * AVAILABLE PARAMETERS:
 * 
 * 1. FEATURES (features object):
 *    - slideshow: boolean - Enable slideshow functionality
 *    - thumbnails: boolean - Show thumbnail navigation
 *    - fullscreen: boolean - Enable fullscreen mode
 *    - zoom: boolean - Enable zoom functionality
 *    - captions: boolean - Show photo captions
 *    - sharing: boolean - Enable sharing buttons
 *    - lazyLoading: boolean - Enable lazy loading for performance
 *    - imageOptimization: boolean - Enable image optimization
 *    - preloadAdjacent: number - Number of adjacent images to preload
 * 
 * 2. LAYOUT (layout object):
 *    - thumbnailSize: number - Size of thumbnails in pixels
 *    - maxDisplay: number|null - Maximum photos to display (null = all)
 *    - columns: 'auto'|number - Number of columns ('auto' or 1, 2, 3, 4, etc.)
 *    - spacing: 'compact'|'default'|'wide' - Spacing between photos
 * 
 * 3. PERFORMANCE (performance object):
 *    - enableIntersectionObserver: boolean - Use Intersection Observer
 *    - rootMargin: number - Root margin for Intersection Observer (pixels)
 *    - enableWebP: boolean - Enable WebP format support
 *    - enableProgressiveLoading: boolean - Enable progressive loading
 * 
 * HOOK RETURNS:
 *    - getGalleryProps: function - Returns props for UnifiedGallery
 *    - loading: boolean - Loading state
 *    - error: string|null - Error message if any
 *    - photos: array - Array of photo objects
 *    - refreshPhotos: function - Function to refresh gallery
 *    - galleryConfig: object - Current configuration
 *    - galleryRef: ref - Reference to gallery component
 */

const RawrockPage = () => {
  // Use standardized gallery hook with ALL POSSIBLE PARAMETERS shown with default values
  // This page serves as a comprehensive example of all available configuration options
  const { getGalleryProps, loading, error, photos, refreshPhotos } = useStandardGallery('rawrock', {
    // FEATURES CONFIGURATION
    features: {
      slideshow: true,              // Enable slideshow functionality
      thumbnails: true,             // Show thumbnail navigation
      fullscreen: true,             // Enable fullscreen mode
      zoom: true,                   // Enable zoom functionality
      captions: true,               // Show photo captions
      sharing: false,               // Enable sharing buttons (default: false)
      
      // PERFORMANCE FEATURES
      lazyLoading: true,            // Enable lazy loading for better performance
      imageOptimization: true,      // Enable image optimization
      preloadAdjacent: 2            // Number of adjacent images to preload (2 on each side)
    },
    
    // LAYOUT CONFIGURATION
    layout: {
      thumbnailSize: 220,           // Size of thumbnails in pixels
      maxDisplay: 10,             // Maximum number of photos to display (null = all)
      columns: 'auto',              // Number of columns: 'auto', 1, 2, 3, 4, etc.
      spacing: 'default'            // Spacing between photos: 'compact', 'default', 'wide'
    },
    
    // PERFORMANCE CONFIGURATION
    performance: {
      enableIntersectionObserver: true,    // Use Intersection Observer for performance
      rootMargin: 50,                      // Root margin for Intersection Observer (pixels)
      enableWebP: true,                    // Enable WebP format support
      enableProgressiveLoading: true       // Enable progressive image loading
    }
  });

  return (
    <div className="page-container-wrapper max-width-default padding-default">
      <div className="page-header">
        <h1>
          <i className="fas fa-mountain"></i>
          Raw Rock - Configuration Example
        </h1>
        <p>Welcome to Raw Rock! This page demonstrates all possible gallery configuration parameters.</p>
      </div>

      <div className="page-content">
        <div className="section">
          <p>Welcome to Raw Rock! Explore the fascinating world of raw rock formations and geological features. These natural formations tell the story of Earth's geological history through millions of years of change.</p>
          
          {/* Gallery Status Information */}
          <div className="gallery-status">
            <p><strong>Gallery Status:</strong></p>
            <ul>
              <li>Photos loaded: {photos.length}</li>
              <li>Loading: {loading ? 'Yes' : 'No'}</li>
              <li>Error: {error || 'None'}</li>
            </ul>
            
            {/* Refresh Button Example */}
            <button 
              className="cta-btn secondary" 
              onClick={refreshPhotos}
              disabled={loading}
            >
              <i className="fas fa-sync-alt"></i>
              {loading ? 'Refreshing...' : 'Refresh Gallery'}
            </button>
          </div>
        </div>

        {/* rawrock Gallery with all configuration options */}
        <div className="section">
          <h2>Gallery Configuration Example</h2>
          <p>This gallery uses all default parameters. You can modify any of these in the useStandardGallery configuration above:</p>
          
          <div className="config-info">
            <h3>Current Configuration:</h3>
            <ul>
              <li><strong>Features:</strong> slideshow, thumbnails, fullscreen, zoom, captions enabled</li>
              <li><strong>Layout:</strong> auto columns, 220px thumbnails, default spacing</li>
              <li><strong>Performance:</strong> lazy loading, WebP support, progressive loading enabled</li>
            </ul>
          </div>
          
          <UnifiedGallery 
            {...getGalleryProps("Raw Rock - All Parameters Example")}
          />
        </div>

        {/* Configuration Examples Section */}
        <div className="section">
          <h2>Configuration Examples</h2>
          <p>Here are some common configuration patterns you can use:</p>
          
          <div className="config-examples">
            <div className="config-example">
              <h3>Portrait Gallery (1 column)</h3>
              <pre>{`const { getGalleryProps } = useStandardGallery('gallery', {
  layout: {
    columns: 1,
    thumbnailSize: 400,
    spacing: 'wide'
  }
});`}</pre>
            </div>
            
            <div className="config-example">
              <h3>Grid Gallery (4 columns)</h3>
              <pre>{`const { getGalleryProps } = useStandardGallery('gallery', {
  layout: {
    columns: 4,
    thumbnailSize: 180,
    spacing: 'compact'
  }
});`}</pre>
            </div>
            
            <div className="config-example">
              <h3>Minimal Gallery (no thumbnails)</h3>
              <pre>{`const { getGalleryProps } = useStandardGallery('gallery', {
  features: {
    slideshow: true,
    thumbnails: false,
    fullscreen: true,
    zoom: false,
    captions: true,
    sharing: false
  }
});`}</pre>
            </div>
            
            <div className="config-example">
              <h3>Performance Optimized</h3>
              <pre>{`const { getGalleryProps } = useStandardGallery('gallery', {
  layout: {
    maxDisplay: 20  // Only show first 20 photos
  },
  performance: {
    enableIntersectionObserver: true,
    rootMargin: 100,
    enableWebP: true,
    enableProgressiveLoading: true
  }
});`}</pre>
            </div>
          </div>
        </div>

        {/* Quick Reference Section */}
        <div className="section">
          <h2>Quick Reference</h2>
          <div className="config-info">
            <h3>Common Parameter Values:</h3>
            <ul>
              <li><strong>columns:</strong> 'auto', 1, 2, 3, 4, 5, 6</li>
              <li><strong>thumbnailSize:</strong> 120, 150, 180, 220 (default), 250, 300, 400</li>
              <li><strong>spacing:</strong> 'compact', 'default', 'wide'</li>
              <li><strong>maxDisplay:</strong> null (all), 10, 20, 50, 100</li>
              <li><strong>preloadAdjacent:</strong> 0, 1, 2 (default), 3, 5</li>
              <li><strong>rootMargin:</strong> 0, 25, 50 (default), 100, 200</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawrockPage;
